import {
  Identifiable,
  Project,
  projectReport,
  ProjectReport,
  projectReportField,
} from "@planzone/dto";
import { HttpClient, HttpMethods } from "../types/httpClient";

function extractFields(attributes: {
  [key: string]: string;
}): ProjectReport["fields"][number][] {
  return Object.values(
    Object.entries(attributes)
      .filter(([key]) => /^fields\./.test(key))
      .reduce<{ [key: string]: ProjectReport["fields"][number] }>(
        (fields, [key, value]) => {
          const currentKey = key.replace(/fields\.(.+)\.(\w+)$/, "$2");
          const name = key.replace(
            new RegExp(`fields\.(.+)\.${currentKey}$`),
            "$1",
          );

          return {
            ...fields,
            [name]: fields[name]
              ? { ...fields[name], [currentKey]: value }
              : { ...projectReportField, value: "", [currentKey]: value },
          };
        },
        {},
      ),
  );
}

function peelAttributes(attributes: { [key: string]: string }): {
  [key: string]: string;
} {
  return Object.entries(attributes).reduce(
    (report, [key, value]) => ({
      ...report,
      [key.replace(/reports\.([a-zA-Z0-9-]+)\./, "")]: value,
    }),
    {},
  );
}

function extractAttributes(peeledAttributes: { [key: string]: string }): {
  [key: string]: string;
} {
  return Object.entries(peeledAttributes)
    .filter(([key]) => /^attributes\./.test(key))
    .reduce<{ [key: string]: string }>(
      (attrs, [key, value]) => ({
        ...attrs,
        [key.replace(/^attributes\./, "")]: value,
      }),
      {},
    );
}

function attributeToReport(attributes: {
  [key: string]: string;
}): Identifiable<ProjectReport> {
  const peeledAttributes = peelAttributes(attributes) as {
    uuid: string;
    name: string;
    author: string;
    submissionDate: string;
  };

  const extractedAttributes = extractAttributes(peeledAttributes);

  return {
    ...Object.entries(peeledAttributes)
      .filter(([key]) => !/^fields\./.test(key) && !/^attributes\./.test(key))
      .reduce(
        (attr, [key, value]) => ({ ...attr, [key]: value }),
        {} as Identifiable<ProjectReport>,
      ),
    fields: extractFields(peeledAttributes),
    ...(Object.keys(extractedAttributes).length > 0
      ? { attributes: extractedAttributes }
      : {}),
  };
}

function splitAttrByReportsId(attributes: {
  [key: string]: string;
}): { [key: string]: string }[] {
  return Object.values(
    Object.entries(attributes).reduce<{
      [key: string]: { [key: string]: string };
    }>((fields, [key, value]) => {
      const uuid = key.replace(/reports\.([a-zA-Z0-9-]+)\.(.+)$/, "$1");

      return {
        ...fields,
        [uuid]: fields[uuid]
          ? { ...fields[uuid], [key]: value }
          : { [key]: value },
      };
    }, {}),
  );
}

function filterReportsAttr(attributes: { [key: string]: string }): {
  [key: string]: string;
} {
  return Object.entries(attributes)
    .filter(([key, value]) => /^reports\./.test(key) && !!value)
    .reduce((fields, [key, value]) => ({ ...fields, [key]: value }), {});
}

function getProjectReports(httpClient: HttpClient) {
  return (project: Identifiable<Project>) =>
    httpClient
      .sendHttpRequest<Identifiable<Project>>({
        endpoint: `/projects/${project.uuid}`,
        method: HttpMethods.GET,
      })
      .then(({ data }) =>
        splitAttrByReportsId(filterReportsAttr(data))
          .map(attributeToReport)
          .map((report) => ({ ...projectReport, ...report })),
      );
}

export { getProjectReports };
